import api from './api';

export interface CaseFile {
  id: number;
  url:string;
  path: string;
  case_id: number;
  filename: string;
  original_filename: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  uploaded_by: number;
  created_at: string;
  updated_at: string;
}

// Matches the actual backend response shape (CasesFilesController::uploadFiles),
// which returns `status`/`file`, not `success`/`files`. Uploaded entries are
// plain { name, path, url } — not full CaseFile records (no id/case_id/etc,
// since these live in case_files.files, not a `documents` row).
export interface UploadedCaseFile {
  name: string;
  path: string;
  url: string;
}

export interface CaseFileUploadResponse {
  status: boolean;
  message: string;
  file?: UploadedCaseFile[];
}

export interface CaseFileDeleteResponse {
  status: boolean;
  message: string;
  file?: UploadedCaseFile[];
}

export interface CaseFilesResponse {
  status: boolean;
  files: string | null;
}

class CaseFilesService {
  async uploadFiles(caseId: number, files: FileList): Promise<CaseFileUploadResponse> {
    const formData = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      formData.append('files[]', files[i]);
    }

    const response = await api.post(`/cases/${caseId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  async getCaseFiles(caseId: number): Promise<CaseFilesResponse> {
    const response = await api.get(`/cases/${caseId}/files`);
    return response.data;
  }

  async getPublicCaseFiles(caseId: number, accessCode: string): Promise<CaseFilesResponse> {
    const response = await api.get(`/public/cases/${caseId}/files`, {
      params: {
        access_code: accessCode,
      },
    });
    return response.data;
  }

  // Deletes a single file from a case's own file list (case_files.files),
  // not the separate `documents` table — identified by path, since these
  // entries have no real database id of their own.
  async deleteCaseFile(caseId: number, path: string): Promise<CaseFileDeleteResponse> {
    const response = await api.delete(`/cases/${caseId}/files`, { data: { path } });
    return response.data;
  }

  async deleteDocument(documentId: number, userId: string): Promise<{ status: boolean; message: string }> {
    const response = await api.delete(`/documents/${documentId}/${userId}`);
    return response.data;
  }

  // Parse files from the JSON string in case response and convert to CaseFile format
  parseFilesFromCase(caseData: any): CaseFile[] {
    if (!caseData.files) return [];
    
    try {
      const parsedFiles = JSON.parse(caseData.files);
      if (!Array.isArray(parsedFiles)) return [];
      
      // Convert the simple file objects to CaseFile format
      return parsedFiles.map((file: any, index: number) => ({
        id: index, // Use index as temporary ID since backend doesn't provide one
        url: file.url || '',
        path: file.path || '',
        case_id: caseData.id || 0,
        filename: file.name || file.filename || '',
        original_filename: file.name || file.original_filename || '',
        file_path: file.path || '',
        file_size: 0, // Not provided in the JSON response
        mime_type: 'application/octet-stream', // Default MIME type
        uploaded_by: caseData.user_id || 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));
    } catch (error) {
      console.error('Error parsing files from case:', error);
      return [];
    }
  }

  getFileUrl(file: CaseFile): string {
    // If the file already has a full URL, return it
    if (file.url && file.url.startsWith('http')) {
      return file.url;
    }
    
    // Otherwise construct the URL from the file_path
    return `${api.defaults.baseURL}/storage/${file.file_path}`;
  }

  getPublicFileUrl(file: CaseFile): string {
    // If the file already has a full URL, return it
    if (file.url && file.url.startsWith('http')) {
      return file.url;
    }
    
    // Otherwise construct the public URL
    return `${api.defaults.baseURL}/public/storage/${file.file_path}`;
  }

  formatFileSize(bytes?: number): string {
    if (!bytes || bytes === 0) return 'Unknown size';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(mimeType?: string, fileName?: string): string {
    // Try to determine from MIME type first
    if (mimeType) {
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType.startsWith('video/')) return 'video';
      if (mimeType.startsWith('audio/')) return 'audio';
      if (mimeType.includes('pdf')) return 'picture_as_pdf';
      if (mimeType.includes('word') || mimeType.includes('document')) return 'description';
      if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'table_chart';
      if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'slideshow';
      if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('tar')) return 'folder_zip';
      if (mimeType.includes('text')) return 'text_snippet';
    }
    
    // Fallback to file extension
    if (fileName) {
      const extension = fileName.split('.').pop()?.toLowerCase() || '';
      
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) return 'video';
      if (['mp3', 'wav', 'ogg', 'flac', 'aac'].includes(extension)) return 'audio';
      if (extension === 'pdf') return 'picture_as_pdf';
      if (['doc', 'docx', 'rtf'].includes(extension)) return 'description';
      if (['xls', 'xlsx', 'csv'].includes(extension)) return 'table_chart';
      if (['ppt', 'pptx'].includes(extension)) return 'slideshow';
      if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) return 'folder_zip';
      if (['txt', 'md'].includes(extension)) return 'text_snippet';
    }
    
    return 'insert_drive_file';
  }
}

export default new CaseFilesService();
